import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox } from '@/shared/inputs';
import { Text } from '@/shared/ui';
import { Spacing } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import FormControlLabel from '@mui/material/FormControlLabel';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

// Boris "rg-consent-row": rows in the rg-consent-doc card are separated by a
// hairline divider. These CMS rows are never the card's last row (the
// DisclaimersCheckboxes privacy row is), so every CMS row keeps its divider.
const CheckboxWithLabelContainer = styled(FormControlLabel)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${Spacing.x1_5}px;
  margin: 0;
  width: 100%;
  padding: 14px 0;
  border-bottom: 1px solid ${BorisScreener.line};
`;

// Boris rg-consent-desc: warm muted ink (#5C5F61), not the app's cool hint gray.
const ConsentText = styled(Text)`
  color: ${BorisScreener.inkSoft};
`;

// Boris rg-consent-title .req: coral asterisk marking a required consent.
const Req = styled.span`
  color: ${BorisScreener.coral};
`;

export type CmsConsents = {
  voluntary: boolean;
  apiData: boolean;
  pcpSharing: boolean;
  controlGroup: boolean;
};

type Props = {
  consents: CmsConsents;
  onChange: (key: keyof CmsConsents, value: boolean) => void;
};

export const CONSENT_KEYS: (keyof CmsConsents)[] = ['voluntary', 'apiData', 'pcpSharing', 'controlGroup'];

const I18N_KEYS: Record<keyof CmsConsents, string> = {
  voluntary: 'create-account.cms-consents.voluntary',
  apiData: 'create-account.cms-consents.api-data',
  pcpSharing: 'create-account.cms-consents.pcp-sharing',
  controlGroup: 'create-account.cms-consents.control-group',
};

const CmsConsentCheckboxes: React.FC<Props> = ({ consents, onChange }) => {
  const { t } = useTranslation();

  return (
    <Container>
      {CONSENT_KEYS.map((key) => (
        <CheckboxWithLabelContainer
          key={key}
          control={
            <Checkbox
              checked={consents[key]}
              onChange={(e) => onChange(key, e.target.checked)}
              sx={{ alignSelf: 'start', padding: 0 }}
              inputProps={{
                'aria-label': t(I18N_KEYS[key]),
                'aria-required': true,
              }}
            />
          }
          label={
            <ConsentText size="p3">
              {t(I18N_KEYS[key])}
              <Req aria-hidden="true"> *</Req>
            </ConsentText>
          }
        />
      ))}
    </Container>
  );
};

export default CmsConsentCheckboxes;
